import { BreakawaySection, BreakawayHullState } from '../../types/breakawayHull';
import { calculateSectionFuelRequirements } from '../calculations/breakawayCalculations';

export interface BreakawayValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  sectionValidations: SectionValidationResult[];
  combinedValidation: CombinedValidationResult;
}

export interface SectionValidationResult {
  sectionId: string;
  sectionName: string;
  isValid: boolean;
  errors: string[];
  warnings: string[];
  missingSystems: string[];
  fuelStatus: FuelValidationStatus;
  powerStatus: PowerValidationStatus;
  driveStatus: DriveValidationStatus;
}

export interface CombinedValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  totalCost: number;
  totalTonnage: number;
  combinedPerformance: {
    maneuver: number;
    jump: number;
    reaction: number;
    totalPower: number;
    totalFuel: number;
  };
}

export interface FuelValidationStatus {
  hasSufficientFuel: boolean;
  required: number;
  allocated: number;
  deficit: number;
  breakdown: {
    reactionDrive: number;
    jumpDrive: number;
    powerPlant: number;
  };
}

export interface PowerValidationStatus {
  hasSufficientPower: boolean;
  required: number;
  available: number;
  deficit: number;
  breakdown: {
    drives: number;
    bridge: number;
    sensors: number;
    other: number;
  };
}

export interface DriveValidationStatus {
  hasDrives: boolean;
  canMoveIndependently: boolean;
  driveTypes: {
    maneuver: boolean;
    jump: boolean;
    reaction: boolean;
  };
  warnings: string[];
}

/**
 * Comprehensive validation for breakaway hull configuration
 */
export const validateBreakawayHull = (breakawayState: BreakawayHullState): BreakawayValidationResult => {
  const errors: string[] = [];
  const warnings: string[] = [];
  const sectionValidations: SectionValidationResult[] = [];

  // Validate overall breakaway hull state
  if (!breakawayState.isBreakaway) {
    errors.push('Breakaway hull system is not enabled');
    return createEmptyValidationResult();
  }

  if (breakawayState.sections.length === 0) {
    errors.push('No breakaway sections configured');
    return createEmptyValidationResult();
  }

  if (breakawayState.sections.length < 2) {
    warnings.push('Breakaway hulls typically require at least 2 sections for meaningful separation');
  }

  // Validate each section
  breakawayState.sections.forEach(section => {
    const sectionValidation = validateSection(section);
    sectionValidations.push(sectionValidation);

    // Collect errors and warnings
    errors.push(...sectionValidation.errors.map(err => `${section.name}: ${err}`));
    warnings.push(...sectionValidation.warnings.map(warn => `${section.name}: ${warn}`));
  });

  // Validate combined configuration
  const combinedValidation = validateCombinedConfiguration(breakawayState);

  // Overall validation
  const isValid = errors.length === 0 && combinedValidation.isValid;

  return {
    isValid,
    errors,
    warnings,
    sectionValidations,
    combinedValidation,
  };
};

/**
 * Validate individual section configuration
 */
export const validateSection = (section: BreakawaySection): SectionValidationResult => {
  const errors: string[] = [];
  const warnings: string[] = [];
  const missingSystems: string[] = [];

  // Required systems validation
  if (!section.bridge) {
    errors.push('Bridge is required for independent operation');
    missingSystems.push('Bridge');
  }

  if (!section.powerPlant) {
    errors.push('Power plant is required for independent operation');
    missingSystems.push('Power Plant');
  }

  // Validate bridge rating if bridge exists
  if (section.bridge) {
    const requiredBridgeRating = Math.ceil(section.size / 100);
    const actualBridgeRating = Math.ceil(section.size / 100); // Simplified calculation
    if (actualBridgeRating < requiredBridgeRating) {
      errors.push(`Bridge rating insufficient (${actualBridgeRating} vs ${requiredBridgeRating} required)`);
    }
  }

  // Validate power plant if it exists
  const powerStatus = validateSectionPower(section);
  if (!powerStatus.hasSufficientPower) {
    errors.push(`Insufficient power (${powerStatus.available} vs ${powerStatus.required} required)`);
  }

  // Validate drives
  const driveStatus = validateSectionDrives(section);
  if (!driveStatus.hasDrives) {
    warnings.push('No drives installed - section cannot move independently');
  }

  // Validate fuel
  const fuelStatus = validateSectionFuel(section);
  if (!fuelStatus.hasSufficientFuel) {
    errors.push(`Insufficient fuel (${fuelStatus.allocated} vs ${fuelStatus.required} required)`);
  }

  // Validate section size
  if (section.size < 100) {
    warnings.push('Section size below recommended minimum of 100 tons');
  }

  if (section.size > 10000) {
    warnings.push('Section size above recommended maximum of 10,000 tons');
  }

  // Validate hull points
  const expectedHullPoints = Math.floor(section.size * 2.5);
  if (section.hullPoints !== expectedHullPoints) {
    warnings.push(`Hull points calculation may be incorrect (${section.hullPoints} vs ${expectedHullPoints} expected)`);
  }

  return {
    sectionId: section.id,
    sectionName: section.name,
    isValid: errors.length === 0,
    errors,
    warnings,
    missingSystems,
    fuelStatus,
    powerStatus,
    driveStatus,
  };
};

/**
 * Validate section power requirements
 */
export const validateSectionPower = (section: BreakawaySection): PowerValidationStatus => {
  let requiredPower = 0;
  const breakdown = {
    drives: 0,
    bridge: 0,
    sensors: 0,
    other: 0,
  };

  // Calculate drive power requirements
  if (section.drives.maneuver) {
    const drivePower = section.drives.maneuver.rating * 5; // 5 Power per maneuver rating
    breakdown.drives += drivePower;
    requiredPower += drivePower;
  }

  if (section.drives.jump) {
    const drivePower = section.drives.jump.rating * 10; // 10 Power per jump rating
    breakdown.drives += drivePower;
    requiredPower += drivePower;
  }

  // Bridge power requirement
  if (section.bridge) {
    breakdown.bridge = 1; // Basic bridge power requirement
    requiredPower += 1;
  }

  // Sensors power requirement
  if (section.sensors) {
    breakdown.sensors = 1; // Basic sensors power requirement
    requiredPower += 1;
  }

  // Other systems power (weapons, etc.)
  if (section.weapons.length > 0) {
    const weaponPower = section.weapons.length * 2; // 2 Power per weapon (simplified)
    breakdown.other += weaponPower;
    requiredPower += weaponPower;
  }

  const availablePower = section.powerPlant?.power || 0;
  const hasSufficientPower = availablePower >= requiredPower;
  const deficit = Math.max(0, requiredPower - availablePower);

  return {
    hasSufficientPower,
    required: requiredPower,
    available: availablePower,
    deficit,
    breakdown,
  };
};

/**
 * Validate section drives
 */
export const validateSectionDrives = (section: BreakawaySection): DriveValidationStatus => {
  const driveTypes = {
    maneuver: !!section.drives.maneuver,
    jump: !!section.drives.jump,
    reaction: !!section.drives.reaction,
  };

  const hasDrives = driveTypes.maneuver || driveTypes.jump || driveTypes.reaction;
  const canMoveIndependently = hasDrives;
  const warnings: string[] = [];

  // Check for drive compatibility issues
  if (driveTypes.maneuver && driveTypes.reaction) {
    warnings.push('Both maneuver and reaction drives installed - may cause operational conflicts');
  }

  if (driveTypes.jump && !driveTypes.maneuver) {
    warnings.push('Jump drive without maneuver drive - limited tactical options');
  }

  if (driveTypes.reaction && !driveTypes.maneuver) {
    warnings.push('Reaction drive without maneuver drive - limited tactical options');
  }

  return {
    hasDrives,
    canMoveIndependently,
    driveTypes,
    warnings,
  };
};

/**
 * Validate section fuel allocation
 */
export const validateSectionFuel = (section: BreakawaySection): FuelValidationStatus => {
  const requirements = calculateSectionFuelRequirements(section);
  const allocated = section.fuelAllocation.tons;
  const hasSufficientFuel = allocated >= requirements.total;
  const deficit = Math.max(0, requirements.total - allocated);

  return {
    hasSufficientFuel,
    required: requirements.total,
    allocated,
    deficit,
    breakdown: {
      reactionDrive: requirements.reactionDrive,
      jumpDrive: requirements.jumpDrive,
      powerPlant: requirements.powerPlant,
    },
  };
};

/**
 * Validate combined configuration
 */
export const validateCombinedConfiguration = (breakawayState: BreakawayHullState): CombinedValidationResult => {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Validate total tonnage
  const totalTonnage = breakawayState.sections.reduce((sum, section) => sum + section.size, 0);
  if (totalTonnage < 200) {
    warnings.push('Combined tonnage below recommended minimum of 200 tons');
  }

  if (totalTonnage > 50000) {
    warnings.push('Combined tonnage above recommended maximum of 50,000 tons');
  }

  // Validate cost
  const totalCost = breakawayState.totalCost;
  if (totalCost > 1000000000) { // 1 billion Cr
    warnings.push('Total cost exceeds 1 billion credits - may be impractical');
  }

  // Validate combined performance
  const combinedPerformance = breakawayState.combinedPerformance;
  
  if (combinedPerformance.maneuver > 6) {
    warnings.push('Combined maneuver rating exceeds 6G - may be unrealistic');
  }

  if (combinedPerformance.jump > 6) {
    warnings.push('Combined jump rating exceeds Jump-6 - may be unrealistic');
  }

  if (combinedPerformance.reaction > 6) {
    warnings.push('Combined reaction rating exceeds 6G - may be unrealistic');
  }

  // Check for performance imbalances
  if (combinedPerformance.maneuver > 0 && combinedPerformance.jump === 0) {
    warnings.push('Maneuver drive without jump capability - limited strategic options');
  }

  if (combinedPerformance.jump > 0 && combinedPerformance.maneuver === 0) {
    warnings.push('Jump drive without maneuver capability - limited tactical options');
  }

  // Validate separation time
  if (breakawayState.separationTime === '0 rounds') {
    errors.push('Separation time cannot be 0 rounds');
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    totalCost,
    totalTonnage,
    combinedPerformance,
  };
};

/**
 * Create empty validation result for error cases
 */
const createEmptyValidationResult = (): BreakawayValidationResult => ({
  isValid: false,
  errors: ['Breakaway hull validation failed'],
  warnings: [],
  sectionValidations: [],
  combinedValidation: {
    isValid: false,
    errors: ['Combined validation failed'],
    warnings: [],
    totalCost: 0,
    totalTonnage: 0,
    combinedPerformance: {
      maneuver: 0,
      jump: 0,
      reaction: 0,
      totalPower: 0,
      totalFuel: 0,
    },
  },
});

/**
 * Get validation summary for display
 */
export const getValidationSummary = (validation: BreakawayValidationResult) => {
  const totalErrors = validation.errors.length;
  const totalWarnings = validation.warnings.length;
  const validSections = validation.sectionValidations.filter(s => s.isValid).length;
  const totalSections = validation.sectionValidations.length;

  return {
    isValid: validation.isValid,
    totalErrors,
    totalWarnings,
    validSections,
    totalSections,
    completionPercentage: totalSections > 0 ? (validSections / totalSections) * 100 : 0,
  };
}; 